import { sureOf } from "../../utils/CommonUtils";
import { TokenUtils } from "../../utils/TokenUtils";
import { TokenJSONElementFigmaTokensTransformer } from "./elements-transformers/SDKToolsJSONElementFigmaTokensTransformer";
import { TokenJSONElementStyleDictionaryTransformer } from "./elements-transformers/SDKToolsJSONElementStyleDictionaryTransformer";
import merge from "lodash/merge";
export var JSONBuilderNamingOption;
(function (JSONBuilderNamingOption) {
    JSONBuilderNamingOption["original"] = "original";
    JSONBuilderNamingOption["camelcase"] = "camelcase";
    JSONBuilderNamingOption["snakecase"] = "snakecase";
    JSONBuilderNamingOption["kebabcase"] = "kebabcase";
})(JSONBuilderNamingOption || (JSONBuilderNamingOption = {}));
var ConsumerMode;
(function (ConsumerMode) {
    ConsumerMode[ConsumerMode["styleDictionary"] = 0] = "styleDictionary";
    ConsumerMode[ConsumerMode["figmaTokens"] = 1] = "figmaTokens";
})(ConsumerMode || (ConsumerMode = {}));
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Tool implementation
/** JSON builder tooling object. Allows to build full token JSON definition for different styles of outputs, like Style Dictionary or Figma Tokens plugin */
export class TokenJSONBuilder {
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Constructor
    constructor(instance, designSystemId, versionId) {
        this.instance = instance;
        this.designSystemId = designSystemId;
        this.versionId = versionId;
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Public interface
    /** Fetches all tokens available for selected design system version, and converts them to style dictionary representation. */
    async styleDictionaryRepresentation(options) {
        this.tokenTransformer = new TokenJSONElementStyleDictionaryTransformer();
        let tokens = await this.instance.tokens.getTokens({
            designSystemId: this.designSystemId,
            versionId: this.versionId,
        });
        const groups = await this.instance.tokens.getTokenGroups({
            designSystemId: this.designSystemId,
            versionId: this.versionId,
        });
        // Filter out tokens that contain brand id
        if (options.brandId) {
            tokens = tokens.filter((t) => t.brandId === options.brandId);
        }
        return this.buildTokenStructure(tokens, groups, {
            ...options,
            ...{ consumerMode: ConsumerMode.figmaTokens },
        });
    }
    /** Fetches all tokens available for selected design system version, and converts them to figma tokens representation. */
    async figmaTokensRepresentation(isSingleFile) {
        if (!isSingleFile) {
            throw new Error("Multi file mode of write is not yet supported by the tooling");
        }
        this.tokenTransformer = new TokenJSONElementFigmaTokensTransformer();
        let tokens = await this.instance.tokens.getTokens({
            designSystemId: this.designSystemId,
            versionId: this.versionId,
        });
        const groups = await this.instance.tokens.getTokenGroups({
            designSystemId: this.designSystemId,
            versionId: this.versionId,
        });
        const brand = await this.instance.brands.getBrands({
            designSystemId: this.designSystemId,
            versionId: this.versionId,
        });
        const options = {
            naming: JSONBuilderNamingOption.original,
            includeComments: false,
            includeBrandId: false,
            brandId: sureOf(brand[0]).id, // Select first brand for now
            includeType: true,
            includeRootTypeNodes: false,
            type: null,
            multifile: false,
            consumerMode: ConsumerMode.figmaTokens,
        };
        // Filter out tokens that contain brand id
        if (options.brandId) {
            tokens = tokens.filter((t) => t.brandId === options.brandId);
        }
        return this.buildTokenStructure(tokens, groups, options);
    }
    buildTokenStructure(tokens, groups, options) {
        if (options.type) {
            // When a specific type is requested, only generate that particular one
            for (const group of groups) {
                if (group.isRoot && group.tokenType === options.type) {
                    return this.generateStyleDictionaryTree(group, tokens, groups, options);
                }
            }
        }
        else {
            // Generate all token types
            let result = {};
            for (const group of groups) {
                if (group.isRoot) {
                    const sdTree = this.generateStyleDictionaryTree(group, tokens, groups, options);
                    result = merge(result, sdTree);
                }
            }
            return result;
        }
        return {};
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Style dictionary manipulation
    /** Generate style dictionary tree */
    generateStyleDictionaryTree(rootGroup, allTokens, allGroups, options) {
        const writeRoot = {};
        const mappedTokens = TokenUtils.tokensToReferenceMap(allTokens);
        // Compute full data structure of the entire type-dependent tree
        const result = this.representTree(rootGroup, allTokens, allGroups, writeRoot, options);
        // Add top level entries which don't belong to any user-defined group
        for (const token of TokenJSONBuilder.tokensOfGroup(rootGroup, allTokens)) {
            // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
            result[this.tokenTransformer.safeTokenName(token, options.naming)] =
                this.tokenTransformer.representToken(token, mappedTokens, allGroups, options);
        }
        // Retrieve tokens with type shell or without it
        if (options.includeRootTypeNodes) {
            return {
                [`${this.tokenTransformer.typeLabel(rootGroup.tokenType)}`]: result,
            };
        }
        return result;
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Tree construction
    /** Construct tree out of one specific group, independent of tree type */
    representTree(rootGroup, allTokens, allGroups, writeObject, options) {
        const newObject = writeObject;
        const mappedTokens = TokenUtils.tokensToReferenceMap(allTokens);
        // Represent one level of groups and tokens inside tree. Creates subobjects and then also information about each token
        for (const groupId of rootGroup.subgroupIds) {
            // Write buffer
            const writeSubObject = {};
            // Add each entry for each subgroup, and represent its tree into it
            const group = allGroups.find((g) => g.id === groupId);
            // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
            newObject[this.tokenTransformer.safeGroupName(group)] =
                // @ts-expect-error TS(2345): Argument of type 'TokenGroup | undefined' is not a... Remove this comment to see the full error message
                this.representTree(group, allTokens, allGroups, writeSubObject, options);
            // Add each entry for each token, writing to the same write root
            // @ts-expect-error TS(2345): Argument of type 'TokenGroup | undefined' is not a... Remove this comment to see the full error message
            for (const token of TokenJSONBuilder.tokensOfGroup(group, allTokens)) {
                // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
                newObject[this.tokenTransformer.safeTokenName(token, options.naming)] =
                    this.tokenTransformer.representToken(token, mappedTokens, allGroups, options);
            }
        }
        return newObject;
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Lookup
    /** Find all tokens that belong to a certain group and retrieve them as objects */
    static tokensOfGroup(containingGroup, allTokens) {
        return allTokens.filter((t) => containingGroup.tokenIds.indexOf(t.id) !== -1);
    }
    /** Retrieve chain of groups up to a specified group, ordered from parent to children */
    static referenceGroupChain(containingGroup, allGroups) {
        let iteratedGroup = containingGroup;
        const chain = [containingGroup];
        while (iteratedGroup.parentGroupId) {
            const parentId = iteratedGroup.parentGroupId;
            const parentGroup = allGroups.find((g) => g.id === parentId);
            // @ts-expect-error TS(2345): Argument of type 'TokenGroup | undefined' is not a... Remove this comment to see the full error message
            chain.push(parentGroup);
            // @ts-expect-error TS(2322): Type 'TokenGroup | undefined' is not assignable to... Remove this comment to see the full error message
            iteratedGroup = parentGroup;
        }
        return chain.reverse();
    }
}
